import { FieldArray } from 'formik';
import { UnitExtraPartsField } from './UnitExtraPartsField';
import { map, uniqueId } from 'lodash';
import React, { useContext, useRef } from 'react';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import styled from 'styled-components/macro';
import { assertNotNull } from '../../../../helpers/nullHelpers';
import { charFromInteger } from '../../../../helpers/stringHelpers';
import { SecondaryButton } from '../../../../shared/Button';
import { DraggableContainer } from '../../../../shared/DraggableContainer';
import { DroppableContainer } from '../../../../shared/DroppableContainer';
import { Expandable } from '../../../../shared/Expandable';
import { FormContext } from '../../../../shared/form/Form';
import { colourNeutral07, colourNeutral09, colourNeutral10 } from '../../../../styling/colours';
import { spacing04 } from '../../../../styling/spacing';
import { DesignMaterialsCalculationsRequest } from '../../../designs/design-form/calculations/materials/designMaterialsCalculationsResponse';
import { AddItemButton } from '../AddItemButton';
import { dragTypes } from '../drag-and-drop/DragEndHandlers';
import { HeatPumpFields } from '../heat-pump/HeatPumpFields';
import {
  getInitialHeatPumpFormModelForDesign,
  getInitialHeatPumpFormModelForQuote,
} from '../heat-pump/heatPumpFormModel';
import { getInitialDesignManifoldDetailsFormModel } from '../manifolds/designManifoldDetailsFormModel';
import { ManifoldFields } from '../manifolds/manifoldFields/ManifoldFields';
import { createDuplicateManifold, ManifoldFormModel } from '../manifolds/manifoldFormModel';
import { QuoteOrDesignFormContext } from '../QuoteOrDesignFormContext';
import { manifoldFieldName, solarFieldName, unitFieldName } from '../quoteOrDesignFormFieldNameHelpers';
import { SolarFields } from '../solar/SolarFields';
import { getInitialSolarFormModel } from '../solar/solarFormModel';
import { UnitFieldContext } from './UnitFieldContext';
import {
  addHeatPumpButtonTestId,
  addManifoldButtonTestId,
  addRadiatorManifoldButtonTestId,
  addSolarButtonTestId,
} from './UnitFields';
import { unitHasHeatPump, unitHasSolar, unitHasUnderfloorHeating } from './unitFormModel';

type Props = {
  isExpanded: boolean;
  designMaterialsCalculationsRequest: DesignMaterialsCalculationsRequest | undefined;
};

export const UnitManifoldsContainer = ({ isExpanded, designMaterialsCalculationsRequest }: Props) => {
  const { unit, unitIndex, designCalculations, totalAreaInMetresSquared } = assertNotNull(useContext(UnitFieldContext));

  const { response } = designCalculations ?? { response: null };
  const hasHeatPump = unitHasHeatPump(unit);
  const hasSolar = unitHasSolar(unit);
  const hasUnderfloorHeating = unitHasUnderfloorHeating(unit);

  const formContext = assertNotNull(useContext(QuoteOrDesignFormContext));
  const { formType, buildTypeCode } = formContext;
  const { manifoldPumpOptions } = formContext.referenceData;

  const isInReadOnlyMode = useContext(FormContext).isDisabled;
  const numberOfManifoldsCreated = useRef(unit.manifolds.length);

  const { setFieldValue } = formContext.formProps;

  const areAllManifoldsExpanded = unit.manifolds.every(({ isExpanded }) => isExpanded);
  const toggleAllManifoldsExpanded = () => {
    unit.manifolds.forEach((_, manifoldIndex) =>
      setFieldValue(manifoldFieldName(unitIndex, manifoldIndex, 'isExpanded'), !areAllManifoldsExpanded),
    );
  };

  const addHeatPump = () => {
    if (formType === 'Quote') {
      setFieldValue(unitFieldName(unitIndex, 'heatSourceTypeCode'), 'HeatPump');
      setFieldValue(unitFieldName(unitIndex, 'heatPump'), getInitialHeatPumpFormModelForQuote());
    } else {
      const totalHeatLossInWatts =
        designCalculations == null || response == null ? null : response.heatLossInWatts + response.downlossInWatts;

      setFieldValue(
        unitFieldName(unitIndex, 'heatPump'),
        getInitialHeatPumpFormModelForDesign({
          areaInMetresSquared: totalAreaInMetresSquared,
          heatLossRateInWattsPerSquareMetre:
            totalAreaInMetresSquared === 0 || totalHeatLossInWatts == null
              ? null
              : Math.round(totalHeatLossInWatts / totalAreaInMetresSquared),
        }),
      );
    }

    if (hasSolar) {
      setFieldValue(solarFieldName(unitIndex, 'includingHotWaterCylinder'), false);
    }
  };

  const getCommonManifoldFields = (): ManifoldFormModel => ({
    manifoldId: null,
    id: uniqueId('manifold'),
    name: '',
    isRadiator: false,
    rooms: [],
    isExpanded: true,
    totalUnderfloorHeatingAreaInMetresSquared: '',
    totalNumberOfPorts: '',
    skirtingHeatingValidation: null,
    roomlessManifold: null,
    pump:
      unit.pumpLocation === 'PumpOnManifold' && manifoldPumpOptions[0] != null ? manifoldPumpOptions[0].value : null,
    designManifoldDetails:
      formType === 'Design' ? getInitialDesignManifoldDetailsFormModel(unit.heatSourceTypeCode, buildTypeCode) : null,
  });

  const createNewManifold = (): ManifoldFormModel => {
    numberOfManifoldsCreated.current += 1;
    // when creating first manifold, set heat source to Boiler
    if (formType === 'Quote' && !unitHasHeatPump(unit) && numberOfManifoldsCreated.current === 1) {
      setFieldValue(unitFieldName(unitIndex, 'heatSourceTypeCode'), 'Boiler');
    }

    return {
      ...getCommonManifoldFields(),
      name: `Manifold ${charFromInteger(numberOfManifoldsCreated.current)}`,
    };
  };

  const createNewRadiatorManifold = (): ManifoldFormModel => ({
    ...getCommonManifoldFields(),
    name: 'Radiator Manifold',
    isRadiator: true,
  });

  const deleteHeatPump = () => {
    setFieldValue(unitFieldName(unitIndex, 'heatPump'), null);
    setFieldValue(unitFieldName(unitIndex, 'heatSourceTypeCode'), 'Boiler');
    setFieldValue(unitFieldName(unitIndex, 'heatPumpExtraParts'), []);

    if (hasSolar) {
      setFieldValue(solarFieldName(unitIndex, 'includingHotWaterCylinder'), true);
    }
  };

  const deleteSolar = () => {
    setFieldValue(unitFieldName(unitIndex, 'solar'), null);
  };

  return (
    <Expandable isExpanded={isExpanded}>
      <DroppableContainer droppableId={unit.id} type={dragTypes.manifold}>
        {placeholder => {
          return (
            <FieldArray name={unitFieldName(unitIndex, 'manifolds')}>
              {arrayHelpers => {
                const addManifold = () => {
                  arrayHelpers.push(createNewManifold());
                };

                const addRadiatorManifold = () => {
                  arrayHelpers.push(createNewRadiatorManifold());
                };

                const duplicateManifoldAtIndex = (index: number) => {
                  const newManifold = createDuplicateManifold(unit.manifolds[index]);
                  arrayHelpers.insert(index + 1, newManifold);
                };

                const deleteManifoldAtIndex = (index: number) => {
                  arrayHelpers.remove(index);
                  const remainingManifolds = unit.manifolds.filter((_, manifoldIndex) => manifoldIndex !== index);

                  if (!unitHasUnderfloorHeating({ ...unit, manifolds: remainingManifolds })) {
                    setFieldValue(unitFieldName(unitIndex, 'underfloorHeatingExtraParts'), []);
                  }
                };

                return (
                  <>
                    <ExpandOrCollapseAllManifoldsButton onClick={toggleAllManifoldsExpanded}>
                      {`${areAllManifoldsExpanded ? 'Collapse' : 'Expand'} all`}
                    </ExpandOrCollapseAllManifoldsButton>
                    <ManifoldsContainer>
                      {map(unit.manifolds, (manifold, index) => {
                        return (
                          <StyledDraggableContainer key={manifold.id} draggableId={manifold.id} index={index}>
                            {(manifoldDragHandleProps: DraggableProvidedDragHandleProps | null) => (
                              <ManifoldFields
                                manifold={manifold}
                                manifoldIndex={index}
                                isExpanded={manifold.isExpanded}
                                dragHandleProps={manifoldDragHandleProps}
                                deleteManifold={() => deleteManifoldAtIndex(index)}
                                duplicateManifold={() => duplicateManifoldAtIndex(index)}
                                pumpOnManifold={unit.pumpLocation === 'PumpOnManifold'}
                                designMaterialsCalculationsRequest={designMaterialsCalculationsRequest}
                              />
                            )}
                          </StyledDraggableContainer>
                        );
                      })}
                      {placeholder}
                    </ManifoldsContainer>
                    {!isInReadOnlyMode && (
                      <>
                        <AddItemToUnitButton onClick={addManifold} data-testid={addManifoldButtonTestId(unitIndex)}>
                          + Add Manifold
                        </AddItemToUnitButton>
                        {unitHasHeatPump(unit) && unit.manifolds.filter(m => m.isRadiator).length === 0 && (
                          <AddItemToUnitButton
                            onClick={addRadiatorManifold}
                            data-testid={addRadiatorManifoldButtonTestId(unitIndex)}
                          >
                            + Add Radiator Manifold
                          </AddItemToUnitButton>
                        )}
                      </>
                    )}
                    {hasUnderfloorHeating && (
                      <UnitExtraPartsField unitIndex={unitIndex} technology="underfloorHeating" />
                    )}
                    {unit.heatPump ? (
                      <HeatPumpFields
                        heatPump={unit.heatPump}
                        isExpanded={unit.heatPump.isExpanded}
                        deleteHeatPump={deleteHeatPump}
                      />
                    ) : (
                      !isInReadOnlyMode && (
                        <AddItemToUnitButton onClick={addHeatPump} data-testid={addHeatPumpButtonTestId(unitIndex)}>
                          + Add Heat Pump
                        </AddItemToUnitButton>
                      )
                    )}
                    {unit.solar ? (
                      <SolarFields
                        solar={unit.solar}
                        unitIndex={unitIndex}
                        isExpanded={unit.solar.isExpanded}
                        deleteSolar={deleteSolar}
                      />
                    ) : (
                      !isInReadOnlyMode && (
                        <AddItemToUnitButton
                          onClick={() =>
                            setFieldValue(
                              unitFieldName(unitIndex, 'solar'),
                              getInitialSolarFormModel(buildTypeCode, hasHeatPump),
                            )
                          }
                          data-testid={addSolarButtonTestId(unitIndex)}
                        >
                          + Add Solar
                        </AddItemToUnitButton>
                      )
                    )}
                  </>
                );
              }}
            </FieldArray>
          );
        }}
      </DroppableContainer>
    </Expandable>
  );
};

const AddItemToUnitButton = styled(AddItemButton)`
  margin-top: ${spacing04};
  margin-bottom: 0;
`;

const ManifoldsContainer = styled.div`
  background-color: ${colourNeutral07};
`;

const ExpandOrCollapseAllManifoldsButton = styled(SecondaryButton)`
  margin-bottom: ${spacing04};
`;

const StyledDraggableContainer = styled(DraggableContainer)`
  background-color: ${colourNeutral10};
  border: solid 2px ${colourNeutral09};
`;
